import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getDeviceId } from '../utils/deviceId';
import { detachPush } from '../services/pushService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const token = localStorage.getItem('token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

export const login = createAsyncThunk('auth/login', async (data, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}/auth/login`, { ...data, deviceId: getDeviceId() });
    // Kirish kodi (org.settings.loginCodeEnabled) — bu qurilma tanilmagan, kod boshqa
    // qurilmaga yuborildi. Token hali berilmagan — LoginPage kod kiritish bosqichiga o'tadi.
    if (response.data.needsCode) {
      return { needsCode: true, challengeToken: response.data.challengeToken, expiresIn: response.data.expiresIn };
    }
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    return user;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Xato yuz berdi');
  }
});

// Kirish kodini tasdiqlash (login()'dan needsCode:true kelgandan keyin) — muvaffaqiyatli
// bo'lsa oddiy login bilan bir xil natija (token+user).
export const verifyLoginCode = createAsyncThunk('auth/verifyLoginCode', async ({ challengeToken, code }, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}/auth/login/verify-code`, { challengeToken, code });
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    return user;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Xato yuz berdi');
  }
});

export const getMe = createAsyncThunk('auth/getMe', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${API_URL}/auth/me`);
    return response.data.user;
  } catch (error) {
    // Faqat HAQIQIY avtorizatsiya xatosida (401 — token yaroqsiz/eskirgan/
    // bekor qilingan) tokenni o'chiramiz. Tarmoq uzilishi yoki server xatosida
    // (masalan mobil ilova fonga o'tib qaytganda, tarmoq hali to'liq
    // ulanmagan bo'lishi mumkin) tokenni saqlab qolamiz — aks holda
    // foydalanuvchi HAQIQATDA tizimga kirgan bo'lsa ham shunchaki vaqtinchalik
    // tarmoq muammosi tufayli login sahifasiga chiqarib tashlanib, "login
    // qilib bo'ldim-u yana logout bo'lib ketdi" holatini yaratardi (ayniqsa
    // ombor mudiri/sotuv agenti kabi mobil rollarda — ilova fon/old rejimga
    // ko'p o'tib turadi).
    const isAuthError = error.response?.status === 401;
    if (isAuthError) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    }
    // isAuthError'ni ham qaytaramiz — App.js (AppInit) shu bilan "haqiqiy
    // 401" (darhol login'ga) va "tarmoq xatosi" (qayta urinish kerak) ni
    // ajratadi (pastdagi getMe.rejected reducer'ga qarang).
    return rejectWithValue({ message: error.response?.data?.message || 'Xato yuz berdi', isAuthError });
  }
});

export const updateProfile = createAsyncThunk('auth/updateProfile', async (data, { rejectWithValue }) => {
  try {
    const response = await axios.put(`${API_URL}/auth/update-profile`, data);
    return response.data.user;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Xato yuz berdi');
  }
});

export const changePassword = createAsyncThunk('auth/changePassword', async (data, { rejectWithValue }) => {
  try {
    const response = await axios.put(`${API_URL}/auth/change-password`, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Xato yuz berdi');
  }
});

// Records the logout server-side (audit) while the token is still valid.
// The caller clears local auth state (logout reducer) after this resolves.
export const logoutUser = createAsyncThunk('auth/logoutUser', async () => {
  await detachPush();   // token hali amal qilyapti — shu qurilmaga push kelishini to'xtatamiz
  try { await axios.post(`${API_URL}/auth/logout`); } catch { /* ignore */ }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    isAuthenticated: false,
    loading: false,
    error: null,
    initialized: false,
    // Ekran qulflash holati — localStorage'da saqlanadi (sahifani yangilash
    // qulflashni o'chirib yubormaydi). True bo'lganda LockScreen overlay chiqadi.
    locked: localStorage.getItem('screenLocked') === '1',
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      state.initialized = true;
      state.locked = false;
      localStorage.removeItem('token');
      localStorage.removeItem('screenLocked');
      delete axios.defaults.headers.common['Authorization'];
    },
    clearError: (state) => {
      state.error = null;
    },
    setOrganization: (state, action) => {
      if (state.user) state.user.organization = { ...state.user.organization, ...action.payload };
    },
    setInitialized: (state) => {
      state.initialized = true;
    },
    // Ekranni qulflaydi — overlay chiqadi, parol so'raydi
    lockScreen: (state) => {
      state.locked = true;
      try { localStorage.setItem('screenLocked', '1'); } catch {}
    },
    // Parol tasdiqlangach overlay yopiladi
    unlockScreen: (state) => {
      state.locked = false;
      try { localStorage.removeItem('screenLocked'); } catch {}
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.needsCode) return;   // kod bosqichiga o'tiladi — auth holati o'zgarmaydi
        state.user = action.payload;
        state.isAuthenticated = true;
        state.initialized = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(verifyLoginCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyLoginCode.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.initialized = true;
      })
      .addCase(verifyLoginCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getMe.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMe.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.initialized = true;
      })
      .addCase(getMe.rejected, (state, action) => {
        state.loading = false;
        const isAuthError = !!action.payload?.isAuthError;
        // Faqat HAQIQIY 401'da (yoki token allaqachon yo'q bo'lsa) darhol
        // "kirmagan" holatga o'tkazamiz — PrivateRoute login'ga yo'naltiradi.
        // Tarmoq xatosida (token hali localStorage'da bor) — initialized'ni
        // ATAYLAB true qilmaymiz: App.js (AppInit) buni qayta urinib ko'radi,
        // shu oraliqda PrivateRoute spinner ko'rsatib turadi (login'ga
        // otib yubormaydi). Barcha urinishlar tugasa ham — App.js
        // setInitialized() bilan majburan yakunlaydi (uzoq muddat oflayn
        // holat uchun, abadiy spinnerda qolib ketmasin).
        if (isAuthError || !localStorage.getItem('token')) {
          state.isAuthenticated = false;
          state.initialized = true;
        }
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        const u = action.payload?.user || action.payload;
        state.user = { ...state.user, ...u };
      });
  },
});

export const { logout, clearError, setInitialized, setOrganization, lockScreen, unlockScreen } = authSlice.actions;
export default authSlice.reducer;
